// NO IMPORTS, DELIBERATELY. The route component this belongs to pulls in the
// two acceptance pages, the API client and i18n, and `import.meta.env` makes
// that whole tree unloadable outside Vite — so the one piece of logic worth
// asserting on its own lives in a module a plain node spec can import.
/**
 * `accept-<slug>-invitation` → `<slug>`; anything else → null.
 *
 * WHY A FUNCTION AND NOT A ROUTE PATTERN. React Router 6.26 cannot express
 * `/accept-:profession-invitation`: a dynamic segment is a WHOLE segment, and
 * a partial one matches NOTHING — `matchPath` returns null rather than
 * throwing, so the route would have shipped silently dead. `/accept-*` is
 * rejected outright. So the route is a whole-segment catch-all and the shape
 * of the segment is checked here.
 *
 * The slug is not restricted to `[a-z-]`: `professionalInviteLink` builds
 * links from the registry's slug, and `traductor_publico` — the profession
 * actually asked for — carries an UNDERSCORE. invite-link.spec.ts pins that
 * case, so a pattern that assumed hyphens would reject the first real third
 * profession.
 */
export function professionSlugFromInvitePath(segment: string): string | null {
  const match = /^accept-(.+)-invitation$/.exec(segment);
  return match != null && match[1].length > 0 ? match[1] : null;
}

/**
 * Whether a pathname is an invitation-acceptance URL for ANY profession.
 *
 * BY SHAPE, not by name. Three places in the portal used to carry a list of
 * profession-named prefixes — the light auth shell, the live-chat exclusion
 * and the theme-init script in index.html — and each one silently excluded a
 * profession nobody had added to it: a traductor would have reached their
 * acceptance page with the authenticated dark theme and a support widget over
 * the form.
 */
export function isProfessionalInvitePath(pathname: string): boolean {
  const segment = pathname.split("/")[1] ?? "";
  return professionSlugFromInvitePath(segment) != null;
}
